/**
 * SHOPIFY STANDARD STOREFRONT EVENTS — PRODUCT VIEW
 *
 * Dispatches `shopify:product:view` when a product card becomes visible, and once
 * per PDP load for the main product — including builder PDPs (bundle and CYO),
 * which have no <product-overview> to source variant state from.
 * See: https://shopify.dev/docs/storefronts/themes/best-practices/standard-events
 *
 * Cards are handled in JS rather than through the `standard_event_data` Liquid
 * filter because collection and search grids build their cards client-side
 * (product-grid.js createProductCardComponent). A Liquid-only approach would cover
 * first paint and miss every filtered, sorted, or paginated render.
 *
 * A card qualifies once both conditions hold, whichever lands second:
 *   1. It has been at least 50% visible.
 *   2. `product-card:initialized` has fired, so product and variant data exist.
 * This mirrors data-layer-product-impressions.js, which tracks GA impressions off
 * the same pair of signals.
 *
 * IDs stay as the numeric Shopify ids the card payload already holds. Per Shopify's
 * payload conventions the event constructors accept raw numbers, strings, or GIDs
 * and normalize them, so no `gid://` prefixing is needed here.
 */

/** @type {WeakSet<HTMLElement>} Cards already reported, so a card fires at most once per render. */
const reported = new WeakSet();

/**
 * Resolve the standard event `context` for a card from the grid that owns it.
 * @param {HTMLElement} card
 * @returns {"page"|"collection"|"search"|"dialog"|"recommendation"}
 */
function resolveCardContext(card) {
  // A card inside an open dialog is a quickview, whatever grid it came from.
  if (card.closest('dialog[open], quickview-modal, quick-add-modal')) return 'dialog';

  const grid = card.closest('product-grid');
  if (grid) {
    const context = grid.dataset.context;
    // Secondary grids are recommendations rather than the page's own listing.
    if (grid.dataset.mainProductGrid !== 'true') return 'recommendation';
    if (context === 'collection' || context === 'search') return context;
  }

  if (card.closest('product-recommendations, product-slider')) return 'recommendation';

  const pageType = window.theme?.request?.page_type;
  if (pageType === 'collection' || pageType === 'search') return pageType;
  return 'page';
}

/**
 * Dispatch shopify:product:view from a specific element.
 * @param {HTMLElement} target - Element the event dispatches from
 * @param {Object} product
 * @param {Object|null} variant
 * @param {string} context
 * @returns {Promise<void>}
 */
async function dispatchProductView(target, product, variant, context) {
  if (!product?.id) return;

  try {
    const [{ ProductViewEvent }, { isVariantFullyAvailable }, { buildSelectedOptions, buildSelectedVariantPayload }] =
      await Promise.all([
        import('@shopify/events'),
        import('@utilities/product'),
        import('@utilities/standard-events'),
      ]);

    target.dispatchEvent(
      new ProductViewEvent({
        context,
        selectedOptions: buildSelectedOptions(product, variant),
        product: {
          id: product.id,
          title: product.title || '',
          handle: product.handle || '',
          // Null when no variant is resolved, which is valid per the payload spec.
          selectedVariant: buildSelectedVariantPayload(variant, product, isVariantFullyAvailable),
        },
      })
    );
  } catch (error) {
    console.warn('StandardEvents: failed to dispatch shopify:product:view', error);
  }
}

// ─── Product cards ────────────────────────────────────────────────────────

/**
 * Report a card once it is both visible and initialized.
 * @param {HTMLElement} card
 */
function tryDispatchCard(card) {
  if (!card._seVisible || !card._seReady) return;
  if (reported.has(card)) return;

  const { product, variant } = card._seData || {};
  if (!product?.id) return;

  reported.add(card);
  intersectionObserver.unobserve(card);

  dispatchProductView(card, product, variant, resolveCardContext(card));
}

const intersectionObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target._seVisible = true;
      tryDispatchCard(entry.target);
    }
  },
  { threshold: 0.5 }
);

// Bubbles from each card to document, so one listener covers every card.
document.addEventListener('product-card:initialized', (event) => {
  const card = event.target;
  card._seData = event.detail;
  card._seReady = true;
  tryDispatchCard(card);
});

// Grid renders replace cards wholesale, so new nodes need observing as they land.
const mutationObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type !== 'childList') continue;
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const cards =
        node.tagName?.toLowerCase() === 'product-card'
          ? [node]
          : Array.from(node.querySelectorAll?.('product-card') || []);
      for (const card of cards) intersectionObserver.observe(card);
    }
  }
});

// ─── Product detail page ──────────────────────────────────────────────────

/**
 * Dispatch a single product view for the PDP's main product.
 *
 * Subscribes to both outcomes of variant resolution because they are mutually
 * exclusive and each clears the other's sticky value: variant-change carries a
 * resolved variant, while no-variant fires when the selection is incomplete, which
 * is the normal state for a product landing with Size unselected. Listening only
 * for variant-change would skip the event entirely on those pages. A null
 * selectedVariant is valid per Shopify's payload spec.
 *
 * Both events are sticky, so subscribing replays the current state even when this
 * module initializes after product-overview. Only the first delivery dispatches;
 * later variant changes belong to shopify:product:select.
 */
async function initProductPage() {
  if (window.theme?.request?.page_type !== 'product') return;

  // Builder PDPs — bundles and CYO — are rooted in their own custom element rather
  // than <product-overview>, so they publish none of its events and the
  // subscription path below never fires. They resolve no single page-level variant
  // either: a bundle's variants belong to its child items, and a CYO build has none
  // until the customer composes one. The view is reported from the element's own
  // product data attributes with a null selectedVariant, which the payload spec
  // allows. Their per-selection changes remain the concern of shopify:product:select.
  const builderRoot = document.querySelector('product-bundle-overview, cyo-product');
  if (builderRoot) {
    const productId = Number(builderRoot.dataset.productId);
    if (Number.isFinite(productId) && productId > 0) {
      dispatchProductView(
        builderRoot,
        {
          id: productId,
          title: builderRoot.dataset.productTitle || '',
          handle: builderRoot.dataset.productHandle || '',
        },
        null,
        'page'
      );
      return;
    }
  }

  try {
    const { subscribe, EVENTS } = await import('@utilities/events');
    let dispatched = false;

    const dispatchOnce = (productOverview, variant) => {
      if (dispatched) return;
      const product = productOverview?.product;
      if (!product?.id) return;

      dispatched = true;
      dispatchProductView(productOverview, product, variant, 'page');
    };

    subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, ({ productOverview, variant }) => {
      dispatchOnce(productOverview, variant);
    });

    subscribe(EVENTS.PRODUCT_OVERVIEW_NO_VARIANT, ({ productOverview }) => {
      dispatchOnce(productOverview, null);
    });
  } catch (error) {
    console.warn('StandardEvents: failed to initialize PDP product view', error);
  }
}

function init() {
  for (const card of document.querySelectorAll('product-card')) {
    intersectionObserver.observe(card);
  }
  mutationObserver.observe(document.body, { childList: true, subtree: true });
  initProductPage();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
